import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, AlertCircle, Target, Zap, Users, TrendingUp, MessageCircle } from 'lucide-react';

const TrainingIndividual = () => {
  const navigate = useNavigate();
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);

  const whatsappNumber = "5521999568656";
  const whatsappMessage = "Olá! Estou no site da mentoria individual e quero mais informações.";
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const problems = [
    'Tem receio de mexer na mesa durante o culto ou evento',
    'Sente que o som fica embolado e difícil de corrigir',
    'Não consegue montar uma boa estrutura de mixagem',
    'Tem dificuldade em fazer ROTEAMENTO',
    'Não entende claramente o signal flow da WING'
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Treinamento 100% Personalizado',
      description: 'Focado nas suas necessidades específicas e no seu ambiente de trabalho'
    },
    {
      icon: Zap,
      title: 'Prática Real',
      description: 'Aplicação direta durante cultos ou eventos reais, não em simulações'
    },
    {
      icon: Users,
      title: 'Acompanhamento Individual',
      description: 'Atenção exclusiva do instrutor oficial Behringer'
    },
    {
      icon: TrendingUp,
      title: 'Resultados Imediatos',
      description: 'Veja melhorias no som desde a primeira sessão'
    }
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Diagnóstico Inicial',
      description: 'Avaliamos suas dificuldades específicas e o setup do seu local'
    },
    {
      step: '02',
      title: 'Plano Personalizado',
      description: 'Criamos um roteiro de treinamento focado nos seus desafios'
    },
    {
      step: '03',
      title: 'Sessões Práticas',
      description: 'Aplicação direta na sua console WING durante eventos reais'
    },
    {
      step: '04',
      title: 'Acompanhamento',
      description: 'Suporte contínuo até você dominar completamente a console'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Back Button */}
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-400 hover:text-yellow-400 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar para página inicial</span>
        </button>
      </div>

      {/* Hero Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(251, 191, 36, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(251, 191, 36, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-yellow-500/10 border border-yellow-500/30 px-6 py-2 rounded-full mb-8">
              <span className="text-yellow-400 font-semibold text-sm">TREINAMENTO PERSONALIZADO</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              <span className="text-white">Treinamento WING</span>
              <br />
              <span className="gradient-text">Individual</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-8 md:mb-12 leading-relaxed px-4">
              Aprenda a dominar a Console WING com orientação exclusiva do instrutor oficial Behringer,
              aplicando <span className="text-yellow-400 font-bold">na prática</span> durante seus cultos e eventos reais.
            </p>

            <a
              href="#precos"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#precos')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-block bg-green-500 hover:bg-green-600 text-white px-8 md:px-10 py-4 md:py-5 rounded-lg font-bold text-base md:text-lg transition-all duration-300 hover:scale-105 glow-green"
            >
              SOLICITAR TREINAMENTO
            </a>
          </div>
        </div>
      </section>

      {/* Para Quem É */}
      <section className="section-padding bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-3xl sm:text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6">
                Para quem é o <span className="gradient-text">Treinamento Individual</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-300 px-4">
                Este treinamento é para você que atua em <span className="text-yellow-400 font-bold">igrejas ou eventos</span>,
                já tem contato com a Behringer WING (ou está começando) e sente que ainda não domina a console com segurança.
              </p>
            </div>

            <div className="glass-dark p-6 md:p-8 lg:p-12 rounded-2xl mb-6 md:mb-8">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Se você:</h3>
              <div className="space-y-3 md:space-y-4">
                {problems.map((problem, index) => (
                  <div key={index} className="flex items-start gap-3 md:gap-4">
                    <AlertCircle className="w-5 h-5 md:w-6 md:h-6 text-red-400 flex-shrink-0 mt-1" />
                    <p className="text-sm sm:text-base md:text-lg text-gray-300">{problem}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-500/10 to-green-500/10 border border-yellow-500/20 p-6 md:p-8 rounded-2xl">
              <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-3 md:mb-4">
                Saiba que isso <span className="text-yellow-400 font-bold">não é falta de talento</span>.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-3 md:mb-4">
                Na maioria dos casos, o problema é a falta de <span className="text-white font-bold">orientação correta e prática</span>.
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-300">
                No Treinamento WING Individual, você aprende a <span className="text-yellow-400 font-bold">pensar como a WING funciona</span>,
                entendendo o caminho do áudio, o motivo de cada ajuste e como aplicar tudo <span className="text-yellow-400 font-bold">na prática</span>,
                direto no culto ou evento real.
              </p>
            </div>

            <div className="text-center mt-8 md:mt-12">
              <div className="inline-block glass-dark px-6 md:px-8 py-4 md:py-6 rounded-xl">
                <p className="text-xl md:text-2xl font-bold text-white mb-2">
                  Nada de decorar botão.
                </p>
                <p className="text-base sm:text-lg md:text-xl text-yellow-400">
                  Aqui o foco é <span className="font-bold">clareza, segurança e resultado</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefícios */}
      <section className="section-padding bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
                Como funciona o <span className="gradient-text">Treinamento</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-16">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="glass p-6 rounded-xl card-hover">
                    <div className="w-14 h-14 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-8 h-8 text-yellow-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Como Funciona - Steps */}
            <div className="grid md:grid-cols-2 gap-6">
              {howItWorks.map((item, index) => (
                <div key={index} className="glass-dark p-6 rounded-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl font-bold text-black">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-400">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Especificações que você vai dominar */}
      <section className="section-padding bg-black relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Title */}
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-3xl md:text-5xl font-bold text-white mb-4">
                Especificações que você vai <span className="gradient-text">dominar</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400 px-4">
                Engineered na Alemanha pelos criadores da X32
              </p>
            </div>

            {/* Specs Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <div className="glass p-6 rounded-xl card-hover">
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">
                  Capacidade de Mixagem
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">48 canais stereo com processamento completo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">16 buses, 8 matrizes e 4 mains - todos stereo</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">376 sinais de entrada e 374 de saída</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Flex-Channels: stereo, mono ou M/S em todos os 48 canais</span>
                  </li>
                </ul>
              </div>

              <div className="glass p-6 rounded-xl card-hover">
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">
                  Processamento de Áudio
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">16 engines de efeitos stereo com +80 FX profissionais</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Processamento de 40-bit para resolução máxima</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">EQ e Dynamics em cada canal e bus</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Midas PRO mic preamps para transparência sem precedentes</span>
                  </li>
                </ul>
              </div>

              <div className="glass p-6 rounded-xl card-hover">
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">
                  Gravação e Interface
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Interface USB de 48 canais para DAW</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Gravação multi-track de 64 canais em SD card</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Controle DAW via HUI e Mackie Control</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Integração com Cubase, Logic, ProTools, Ableton e mais</span>
                  </li>
                </ul>
              </div>

              <div className="glass p-6 rounded-xl card-hover">
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">
                  Conectividade
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">StageConnect exclusivo para até 32 canais via XLR único</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">3 portas AES50 para até 144 I/O</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Dante, Waves SoundGrid via cartões de expansão</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">Apps gratuitos para iOS, Android, Windows, Mac e Linux</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Highlight Box */}
            <div className="bg-gradient-to-r from-yellow-500/10 via-transparent to-green-500/10 border border-yellow-500/20 p-8 md:p-12 rounded-2xl">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">48</div>
                  <div className="text-gray-300">Canais Stereo Completos</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">376</div>
                  <div className="text-gray-300">Sinais de Entrada</div>
                </div>
                <div>
                  <div className="text-5xl font-bold text-yellow-400 mb-2">40-bit</div>
                  <div className="text-gray-300">Processamento de Áudio</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Wing Models Section */}
      <section className="section-padding bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        {/* Background Image - Família WING com transparência */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_behringer-school/artifacts/gnfl24lz_familia%20wing.png"
            alt="Família WING"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-black/70 to-black/90"></div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Title */}
            <div className="text-center mb-16">
              <div className="inline-block bg-yellow-500/10 border border-yellow-500/30 px-6 py-2 rounded-full mb-4">
                <span className="text-yellow-400 font-semibold text-sm">3 MODELOS PROFISSIONAIS</span>
              </div>
              <h2 className="text-3xl sm:text-3xl md:text-5xl font-bold text-white mb-4">
                Aprenda a operar <span className="gradient-text">todos os modelos</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-gray-400 px-4">
                O treinamento individual abrange as 3 versões da console WING
              </p>
            </div>

            {/* Models Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
              <div className="glass rounded-2xl overflow-hidden card-hover group">
                <div className="relative bg-gradient-to-br from-black to-gray-900 p-4 md:p-8 h-48 md:h-64 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_behringer-school/artifacts/1p4mlkub_02.png"
                    alt="WING Full"
                    className="w-full h-full object-contain relative z-10 transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">WING Full</h3>
                  <p className="text-sm md:text-base text-gray-400 mb-4">A console completa com controle total e máxima flexibilidade</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">48 canais stereo completos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Tela touchscreen de 10"</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Ideal para FOH e monitor</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="glass rounded-2xl overflow-hidden card-hover group">
                <div className="relative bg-gradient-to-br from-black to-gray-900 p-4 md:p-8 h-48 md:h-64 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_behringer-school/artifacts/09x1i72v_FRONT%20C.png"
                    alt="WING Compact"
                    className="w-full h-full object-contain relative z-10 transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">WING Compact</h3>
                  <p className="text-sm md:text-base text-gray-400 mb-4">Design compacto sem comprometer recursos profissionais</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Todos os recursos da WING</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Formato mais portátil</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Perfeita para touring</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="glass rounded-2xl overflow-hidden card-hover group">
                <div className="relative bg-gradient-to-br from-black to-gray-900 p-4 md:p-8 h-48 md:h-64 flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_behringer-school/artifacts/sjpepth5_03.png"
                    alt="WING Rack"
                    className="w-full h-full object-contain relative z-10 transform group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2">WING Rack</h3>
                  <p className="text-sm md:text-base text-gray-400 mb-4">Solução rack mount para instalações fixas e broadcast</p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Montagem em rack padrão</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Controle 100% via apps</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 md:w-5 md:h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-xs md:text-sm">Ideal para igrejas e teatros</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="text-center glass-dark p-6 md:p-8 rounded-2xl">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                Domine qualquer modelo WING com <span className="gradient-text">confiança total</span>
              </h3>
              <p className="text-sm sm:text-base md:text-lg text-gray-300 mb-6 px-4">
                O treinamento individual se adapta ao modelo que você tem ou vai adquirir, preparando você para trabalhar com qualquer configuração.
              </p>
              <a
                href="https://mpago.la/2LnSDaL"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black px-8 py-4 rounded-lg font-bold transition-all duration-300 hover:scale-105 glow-yellow"
              >
                COMEÇAR MEU TREINAMENTO
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="section-padding bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-3xl md:text-5xl font-bold text-white mb-4">
                Investimento no seu <span className="gradient-text">Desenvolvimento</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-400">
                Treinamento personalizado com resultados garantidos
              </p>
            </div>

            <div className="glass-dark border-2 border-yellow-500/30 rounded-2xl p-8 md:p-12 text-center glow-yellow">
              <div className="mb-8">
                <div className="inline-block bg-yellow-500/10 border border-yellow-500/30 px-6 py-2 rounded-full mb-6">
                  <span className="text-yellow-400 font-semibold text-sm">TREINAMENTO INDIVIDUAL</span>
                </div>

                <div className="mb-6">
                  <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                    6x de R$ 266,00
                  </div>
                  <div className="text-sm md:text-base font-bold text-yellow-400 mb-2">
                    R$ 1.600 ou à vista R$ 1.449,00
                  </div>
                  <div className="text-lg md:text-xl text-gray-400">
                    Economia de R$ 151,00 no pagamento à vista
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-500/10 to-green-500/10 border border-yellow-500/20 rounded-xl p-6 mb-8">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-4">O que está incluído:</h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-left">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Diagnóstico completo da sua cena</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Plano de treinamento personalizado</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Montamos juntos sua estrutura de mixagem</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Acompanhamento individual exclusivo</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Suporte direto com o instrutor</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
                      <span className="text-gray-300">Material complementar exclusivo</span>
                    </div>
                  </div>
                </div>

                <a
                  href="https://mpago.la/2LnSDaL"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-green-500 hover:bg-green-600 text-white px-10 py-5 rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105 glow-green"
                >
                  COMPRAR MENTORIA
                </a>

                {/* Escolha de datas */}
                <div className="mt-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <p className="text-green-400 font-semibold text-base md:text-lg flex items-center justify-center gap-2">
                    <span>📅</span>
                    Aqui você pode escolher as datas e horário para sua aula!
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Garantia de satisfação ou seu dinheiro de volta</span>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-400 text-sm">
                💳 Pagamento seguro via cartão de crédito, PIX ou boleto
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 hover:scale-110 glow-green"
        onMouseEnter={() => setShowWhatsAppTooltip(true)}
        onMouseLeave={() => setShowWhatsAppTooltip(false)}
        aria-label="Falar no WhatsApp"
      >
        <MessageCircle className="w-8 h-8" />
        
        {/* Tooltip */}
        {showWhatsAppTooltip && (
          <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium shadow-lg">
            Fale conosco no WhatsApp
            <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
              <div className="w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-gray-900"></div>
            </div>
          </div>
        )}
        
        {/* Pulse Animation */}
        <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75"></span>
      </a>
    </div>
  );
};

export default TrainingIndividual;
